import React, { useMemo } from "react";
import { Checkbox, Box, Typography } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { RichTreeView } from "@mui/x-tree-view/RichTreeView";
import { TreeItem2 } from "@mui/x-tree-view/TreeItem2";

export default function GeoSelector({ title, items, selected, onChange, width }) {
  const selectedSet = useMemo(() => new Set(selected || []), [selected]);

  const idToNode = useMemo(() => {
    const m = new Map();
    const walk = (nodes) => {
      (nodes || []).forEach((n) => {
        m.set(n.id, n);
        if (n.children) walk(n.children);
      });
    };
    walk(items || []);
    return m;
  }, [items]);

  const allValues = useMemo(() => {
    const s = new Set();
    idToNode.forEach((n) => {
      if (n.value !== undefined) s.add(n.value);
    });
    return [...s];
  }, [idToNode]);

  const allChecked = allValues.length > 0 && allValues.every((v) => selectedSet.has(v));
  const someChecked = allValues.some((v) => selectedSet.has(v));
  const handleSelectAll = () => onChange?.(allChecked ? [] : [...allValues]);

  function GeoItem({ id, itemId, label, children }) {
    const node = idToNode.get(itemId);
    if (!node) {
      return (
        <TreeItem2 id={id} itemId={itemId} label={label}>
          {children}
        </TreeItem2>
      );
    }
    const isParent = Boolean(node.children?.length);
    const childValues = isParent ? node.children.map((c) => c.value).filter(Boolean) : [];
    const checked = isParent
      ? childValues.length > 0 && childValues.every((v) => selectedSet.has(v))
      : selectedSet.has(node.value);
    const indeterminate = isParent && childValues.some((v) => selectedSet.has(v)) && !checked;

    const handleCheckbox = () => {
      const next = new Set(selectedSet);
      if (isParent) {
        const any = childValues.some((v) => selectedSet.has(v));
        childValues.forEach((v) => (any ? next.delete(v) : next.add(v)));
      } else {
        next.has(node.value) ? next.delete(node.value) : next.add(node.value);
      }
      onChange?.([...next]);
    };

    return (
      <TreeItem2
        id={id}
        itemId={itemId}
        label={
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Checkbox
              checked={checked}
              indeterminate={indeterminate}
              onChange={handleCheckbox}
              onClick={(e) => e.stopPropagation()}
              size="small"
            />
            <Typography variant="body2">{label}</Typography>
          </Box>
        }
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </TreeItem2>
    );
  }

  return (
    <Box>
      {title && (
        <Typography variant="subtitle2" gutterBottom>
          {title}
        </Typography>
      )}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5, ml: 0.5 }}>
        <Checkbox checked={allChecked} indeterminate={someChecked && !allChecked} onChange={handleSelectAll} size="small" />
        <Typography variant="body2" fontWeight={600}>
          Select All
        </Typography>
      </Box>
      <RichTreeView
        items={items || []}
        slots={{ item: GeoItem, expandIcon: ChevronRightIcon, collapseIcon: ExpandMoreIcon }}
        disableSelection
        sx={{ width, maxHeight: 300, overflow: "auto" }}
      />
    </Box>
  );
}
